import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronLeft, ChevronRight, ChevronDown, LogOut, Bell, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * One layout shell for all roles (docs/DESIGN_SYSTEM.md §4.11). Replaces the 5 copy-pasted
 * layouts. Role-specific bits (nav config, red-flag badge, wallet, API-config, AI assistant)
 * come in as props/slots so each role's wrapper stays thin.
 *
 * variant="sidebar"      -> admin / seller / pharmeasy (collapsible desktop sidebar + mobile drawer)
 * variant="mobile-tabs"  -> rider / hub-operator (sticky header + fixed bottom tabs)
 *
 * The root sets data-brand so shadcn `--primary` (and nav-active `bg-primary`) resolves to the
 * right brand color: green roles pass dataBrand="pharmeasy", everyone else uses the teal default.
 */
const SIDEBAR_KEY = "edgelms_sidebar_expanded";

function Avatar({ user, fallback = "U", className }) {
  const initial = user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || fallback;
  return (
    <div className={cn("flex items-center justify-center rounded-full text-white font-semibold shrink-0", className)}>
      {initial}
    </div>
  );
}

export function AppLayout({
  variant = "sidebar",
  accent = "teal", // 'teal' | 'green' | 'orange' — mobile-tabs active color + avatar
  navKey = "default", // namespaces the per-section collapse persistence (e.g. 'seller' | 'admin')
  dataBrand, // 'pharmeasy' for green roles; undefined => teal default
  brand, // { name, subtitle, logo? (ReactNode) }
  nav = [], // [{ name, path, icon, testId, badge?, pulse?, end?, tag? }] or section rows { section, tag? }
  user,
  onLogout,
  profilePath,
  topBar = null,
  footerExtra = null, // e.g. seller wallet button (sidebar, above profile)
  drawerExtra = null, // e.g. "Switch to..." button (sidebar mobile drawer)
  headerBadge = null, // mobile-tabs: status pill next to name
  headerSubtitle, // mobile-tabs subtitle line
  assistant = null, // e.g. <EdgeAIAssistant /> rendered consistently when provided
  children,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path, end) =>
    end || path.split("/").length <= 2 ? location.pathname === path : location.pathname.startsWith(path);

  // ---------- mobile-tabs (rider / hub-operator) ----------
  if (variant === "mobile-tabs") {
    return <MobileTabsLayout {...{ accent, brand, nav, user, onLogout, headerBadge, headerSubtitle, isActive, children }} />;
  }

  // ---------- sidebar (admin / seller / pharmeasy) ----------
  return (
    <SidebarLayout
      {...{
        dataBrand, brand, nav, navKey, user, onLogout, profilePath, topBar, footerExtra, drawerExtra, assistant,
        navigate, isActive, children,
      }}
    />
  );
}

function SidebarLayout({
  dataBrand, brand, nav, navKey, user, onLogout, profilePath, topBar, footerExtra, drawerExtra, assistant,
  navigate, isActive, children,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) !== "false"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, String(expanded)); } catch {}
  }, [expanded]);

  // Nav search (transient — resets on navigation) + per-section collapse (persisted per portal).
  const [query, setQuery] = useState("");
  const COLLAPSE_KEY = `${SIDEBAR_KEY}_collapsed_${navKey}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}") || {}; } catch { return {}; }
  });
  const toggleSection = (section) =>
    setCollapsed((prev) => {
      const next = { ...prev, [section]: !prev[section] };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });

  // Flat nav -> groups: a leading no-section group, then one group per { section } divider row.
  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    for (const it of nav) {
      if (it.section) { cur = { section: it.section, tag: it.tag, items: [] }; out.push(cur); }
      else { if (!cur) { cur = { section: null, tag: null, items: [] }; out.push(cur); } cur.items.push(it); }
    }
    return out;
  }, [nav]);

  const NavButton = ({ item, onClick, showLabel }) => {
    const Icon = item.icon;
    const active = isActive(item.path, item.end);
    const danger = item.badge > 0;
    return (
      <button
        key={item.path}
        data-testid={item.testId}
        onClick={onClick}
        title={!showLabel ? item.name : ""}
        className={cn(
          "w-full flex items-center py-3 rounded-lg mb-1 transition-colors",
          showLabel ? "gap-3 px-4" : "justify-center px-0",
          active ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"
        )}
      >
        <span className="relative">
          <Icon className={cn("w-5 h-5", !active && danger && "text-red-500")} />
          {item.badge > 0 && (
            // `pulse` is opt-in per item: it blinks the badge to pull the eye to work
            // that is waiting on this admin (see Pickup Requests in AdminLayout).
            // Items that just carry a count — Ops Red Flags — stay static as before.
            // motion-reduce turns the blink off for anyone who has asked the OS for
            // less animation; the red badge still reads fine without it.
            <span
              role="status"
              aria-label={`${item.badge} ${item.name} need attention`}
              className={cn(
                "absolute -top-2 -right-2 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1",
                item.pulse && "animate-pulse motion-reduce:animate-none"
              )}
            >
              {item.badge > 99 ? "99+" : item.badge}
            </span>
          )}
        </span>
        {showLabel && (
          <span className={cn("text-sm font-medium", !active && danger && "text-red-600")}>{item.name}</span>
        )}
        {showLabel && item.tag && (
          <span className={cn(
            "ml-auto text-xs font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5",
            active ? "bg-white/20 text-primary-foreground" : "bg-brand/10 text-brand"
          )}>
            {item.tag}
          </span>
        )}
      </button>
    );
  };

  // Section rows ({ section, tag? }) render as group labels when labels are visible,
  // and as plain dividers in the collapsed icon rail.
  const NavSection = ({ item, showLabel }) =>
    showLabel ? (
      <p className="flex items-center gap-1.5 px-4 pt-4 pb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
        {item.section}
        {item.tag && (
          <span className="text-xs font-bold rounded-full bg-brand/10 text-brand px-1.5 py-px tracking-wider">
            {item.tag}
          </span>
        )}
      </p>
    ) : (
      <div className="my-3 border-t border-slate-200" />
    );

  // Full labelled nav with a search box + collapsible sections. Used in the expanded desktop
  // sidebar and the mobile drawer. The collapsed icon-rail keeps the plain flat render below.
  const renderTree = ({ mobile }) => {
    const q = query.trim().toLowerCase();
    const visible = groups
      .map((g) => {
        const sectionMatch = !!q && !!g.section && g.section.toLowerCase().includes(q);
        const items = q ? g.items.filter((it) => sectionMatch || (it.name || "").toLowerCase().includes(q)) : g.items;
        return { ...g, items };
      })
      .filter((g) => !q || g.items.length > 0);

    return (
      <>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search menu…"
            aria-label="Search navigation"
            data-testid="nav-search"
            className="w-full h-9 pl-9 pr-8 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {visible.map((g) => {
          const isCollapsed = !q && g.section && collapsed[g.section];
          return (
            <div key={g.section || "_top"}>
              {g.section && (
                <button
                  type="button"
                  onClick={() => toggleSection(g.section)}
                  aria-expanded={!isCollapsed}
                  className="w-full flex items-center gap-1.5 px-4 pt-4 pb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <ChevronDown className={cn("w-3.5 h-3.5 shrink-0 transition-transform", isCollapsed && "-rotate-90")} />
                  <span className="truncate">{g.section}</span>
                  {g.tag && (
                    <span className="ml-1 text-xs font-bold rounded-full bg-brand/10 text-brand px-1.5 py-px tracking-wider normal-case">
                      {g.tag}
                    </span>
                  )}
                </button>
              )}
              {!isCollapsed &&
                g.items.map((item) => (
                  <NavButton
                    key={item.path}
                    item={mobile ? { ...item, testId: item.testId ? `mobile-${item.testId}` : undefined } : item}
                    showLabel
                    onClick={() => { navigate(item.path); if (mobile) setMobileOpen(false); }}
                  />
                ))}
            </div>
          );
        })}

        {q && visible.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-slate-400">No menu items match “{query}”.</p>
        )}
      </>
    );
  };

  const LogoBlock = ({ compact }) => (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-white font-bold text-base overflow-hidden">
        {brand?.logo ?? brand?.name?.charAt(0) ?? "E"}
      </div>
      {!compact && (
        <div>
          <h1 className="text-lg font-bold text-slate-800">{brand?.name ?? "EdgeLMS"}</h1>
          {brand?.subtitle && <p className="text-xs text-slate-500">{brand.subtitle}</p>}
        </div>
      )}
    </div>
  );

  return (
    <div data-brand={dataBrand} className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden md:flex flex-col bg-white border-r border-slate-200 fixed left-0 top-0 bottom-0 z-50 transition-all duration-300",
          expanded ? "w-64 min-w-64 max-w-64" : "w-20 min-w-20 max-w-20"
        )}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          {expanded ? (
            <>
              <LogoBlock />
              <button onClick={() => setExpanded(false)} className="p-1 hover:bg-slate-100 rounded transition-colors" aria-label="Collapse sidebar">
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
            </>
          ) : (
            <button onClick={() => setExpanded(true)} className="w-full flex justify-center p-1 hover:bg-slate-100 rounded transition-colors" aria-label="Expand sidebar">
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          )}
        </div>

        <nav className="flex-1 py-6 px-4 overflow-y-auto">
          {expanded
            ? renderTree({ mobile: false })
            : nav.map((item) =>
                item.section ? (
                  <NavSection key={`section-${item.section}`} item={item} showLabel={false} />
                ) : (
                  <NavButton key={item.path} item={item} showLabel={false} onClick={() => navigate(item.path)} />
                )
              )}
        </nav>

        <div className="p-4 border-t border-slate-200">
          {footerExtra}
          <button
            onClick={() => profilePath && navigate(profilePath)}
            className={cn("w-full mb-3 flex items-center py-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors", expanded ? "gap-3 px-4" : "justify-center px-0")}
            title={!expanded ? user?.name || "Profile" : ""}
          >
            <Avatar user={user} className="w-10 h-10 bg-primary text-sm" />
            {expanded && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-semibold text-slate-800 truncate">{user?.name || "User"}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email || ""}</p>
              </div>
            )}
          </button>
          <button
            onClick={onLogout}
            className={cn("w-full flex items-center py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors justify-center", expanded ? "gap-2" : "")}
            title={!expanded ? "Logout" : ""}
          >
            <LogOut className="w-5 h-5" />
            {expanded && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile top bar + drawer */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-50">
        <LogoBlock />
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 flex flex-col">
            <SheetTitle className="sr-only">{brand?.name ?? "Navigation"} menu</SheetTitle>
            <div className="flex-1 overflow-y-auto py-6 px-4 min-h-0">
              {renderTree({ mobile: true })}
            </div>
            <div className="flex-shrink-0 border-t border-slate-200 p-4 bg-white">
              {drawerExtra && <div onClick={() => setMobileOpen(false)}>{drawerExtra}</div>}
              {footerExtra && <div onClick={() => setMobileOpen(false)}>{footerExtra}</div>}
              <button
                onClick={() => { profilePath && navigate(profilePath); setMobileOpen(false); }}
                className="w-full mb-3 flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <Avatar user={user} className="w-10 h-10 bg-primary text-sm" />
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-semibold text-slate-800 truncate">{user?.name || "User"}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email || ""}</p>
                </div>
              </button>
              <button
                onClick={() => { onLogout(); setMobileOpen(false); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Main — min-w-0 lets this flex child shrink below wide content (e.g. data tables)
          instead of overflowing the viewport; inner overflow-x-auto regions scroll themselves. */}
      <main className={cn("flex-1 min-w-0 mt-16 md:mt-0 transition-all duration-300", expanded ? "md:ml-64" : "md:ml-20")}>
        {topBar}
        {/* max-w keeps content readable on very wide displays; below the cap it's a no-op.
            Extra bottom padding when the floating assistant is present so page content
            (tables, pagers, action bars) never sits under the fixed bottom-right FAB. */}
        <div
          className={cn(
            "px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 lg:pt-8 max-w-[1600px] mx-auto",
            assistant ? "pb-24" : "pb-4 sm:pb-6 lg:pb-8"
          )}
        >
          {children}
        </div>
      </main>
      {assistant}
    </div>
  );
}

const TABS_ACCENT = {
  orange: { avatar: "bg-orange-500", active: "text-orange-600", iconBg: "bg-orange-100", iconText: "text-orange-600" },
  green: { avatar: "bg-emerald-600", active: "text-emerald-600", iconBg: "bg-emerald-100", iconText: "text-emerald-600" },
  teal: { avatar: "bg-[#00A699]", active: "text-[#00A699]", iconBg: "bg-teal-100", iconText: "text-teal-700" },
};

function MobileTabsLayout({ accent, brand, nav, user, onLogout, headerBadge, headerSubtitle, isActive, children }) {
  const [showMenu, setShowMenu] = useState(false);
  const a = TABS_ACCENT[accent] || TABS_ACCENT.orange;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-white shadow-sm sticky top-0 z-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar user={user} fallback={brand?.name?.charAt(0) || "U"} className={cn("w-10 h-10", a.avatar)} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-800 text-sm truncate">{user?.name || brand?.name || "User"}</p>
                {headerBadge}
              </div>
              <p className="text-xs text-slate-500 truncate">{headerSubtitle || "Pharmeasy"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
              <Bell className="w-5 h-5 text-slate-600" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setShowMenu((v) => !v)} aria-label="Menu">
              {showMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
        {showMenu && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t z-50">
            <div className="p-4 border-b">
              <div className="flex items-center gap-3">
                <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", a.iconBg)}>
                  <span className={cn("font-bold", a.iconText)}>{user?.name?.charAt(0)?.toUpperCase() || "U"}</span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{user?.name}</p>
                  <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                  {user?.mobile && <p className="text-xs text-slate-400">{user.mobile}</p>}
                </div>
              </div>
            </div>
            <button onClick={() => { setShowMenu(false); onLogout(); }} className="w-full p-4 flex items-center gap-3 text-red-600 hover:bg-red-50">
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 p-4 pb-24">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40">
        <div className="flex justify-around items-center py-2">
          {nav.filter((item) => !item.section).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.end);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId || `nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn("flex flex-col items-center py-2 px-3 rounded-lg transition-colors min-w-[56px]", active ? a.active : "text-slate-500 hover:text-slate-700")}
              >
                <Icon className={cn("w-6 h-6", active && "stroke-[2.5]")} />
                <span className={cn("text-xs mt-1", active && "font-semibold")}>{item.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default AppLayout;
